import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { API_BASE } from '../lib/apiBase';

type State = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<State>('loading');
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendError, setResendError] = useState('');
  const token = searchParams.get('token');
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!token) { setState('error'); return; }
    fetch(`${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(res => { setState(res.ok ? 'success' : 'error'); })
      .catch(() => { setState('error'); });
  }, [token]);

  async function resend() {
    setResending(true);
    setResendError('');
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (res.ok) {
        setResendDone(true);
      } else {
        const body = await res.json() as { error?: { message: string } };
        setResendError(body.error?.message ?? 'Could not resend. Please try again.');
      }
    } catch {
      setResendError('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#f9fafb',
    }}>
      <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: 36,
          background: state === 'loading' ? '#f3f4f6'
            : state === 'success' ? '#dcfce7'
            : '#fee2e2',
        }}>
          {state === 'loading' ? '⏳' : state === 'success' ? '✅' : '❌'}
        </div>

        {state === 'loading' && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
              Verifying your email…
            </h1>
            <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Just a moment.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#15803d', margin: '0 0 8px' }}>
              Your email is verified!
            </h1>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 24px' }}>
              Your Housewizrd account is now fully active.
            </p>
            <Link
              to="/app/dashboard"
              style={{
                display: 'inline-block',
                background: '#9333EA',
                color: '#fff',
                fontWeight: 600,
                fontSize: 15,
                padding: '12px 28px',
                borderRadius: 10,
                textDecoration: 'none',
              }}
            >
              Go to dashboard
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#dc2626', margin: '0 0 8px' }}>
              Link expired or invalid
            </h1>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 24px' }}>
              This verification link has expired or is invalid. Request a new one below.
            </p>

            {resendDone ? (
              <p style={{ color: '#15803d', fontSize: 14, fontWeight: 500 }}>
                Verification email sent — check your inbox.
              </p>
            ) : (
              <>
                <button
                  onClick={resend}
                  disabled={resending}
                  style={{
                    background: resending ? '#d1d5db' : '#9333EA',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 15,
                    padding: '12px 28px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: resending ? 'not-allowed' : 'pointer',
                  }}
                >
                  {resending ? 'Sending…' : 'Resend verification email'}
                </button>
                {resendError && (
                  <p style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{resendError}</p>
                )}
                {!accessToken && (
                  <p style={{ color: '#6b7280', fontSize: 13, marginTop: 16 }}>
                    You must be{' '}
                    <Link to="/login" style={{ color: '#9333EA', textDecoration: 'underline' }}>
                      signed in
                    </Link>{' '}
                    to resend.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
